import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FileText, ArrowLeft, Trash2, Building, User,
  Mail, Phone, AlertCircle, Clock, X, Edit
} from 'lucide-react';
import api from '../../api/apiClient';

interface LeaseDetail {
  id: number;
  propertyAddress: string;
  propertyId: number;
  status: string;
  startDate: string;
  endDate: string;
  rentAmount: number;
  rentFrequency: string;
  securityDeposit?: number;
  daysUntilExpiry?: number;
  ownerName?: string;
  tenantName?: string;
  tenantEmail?: string;
  property?: {
    id: number;
    address: string;
    type: string;
    bedrooms?: number;
    bathrooms?: number;
  };
  tenant?: {
    id: number;
    name: string;
    email: string;
    phone?: string;
  };
}

export default function AgentLeaseDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [lease, setLease] = useState<LeaseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [terminating, setTerminating] = useState(false);
  const [terminateReason, setTerminateReason] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) {
      loadLease();
    }
  }, [id]);

  const loadLease = async () => {
    setLoading(true);
    try {
      const response = await api.get<LeaseDetail>(`/agent/leases/${id}`);
      setLease(response);
    } catch (err) {
      console.error('Failed to load lease:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError('');
    try {
      await api.delete(`/agent/leases/${id}`);
      navigate('/agent/leases');
    } catch (err: any) {
      console.error('Failed to delete lease:', err);
      setError(err.message || 'Failed to delete lease');
      setShowDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleTerminate = async () => {
    setTerminating(true);
    try {
      await api.post(`/agent/leases/${id}/terminate`, { reason: terminateReason });
      setShowTerminateModal(false);
      loadLease();
    } catch (err) {
      console.error('Failed to terminate lease:', err);
    } finally {
      setTerminating(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE': return 'bg-green-100 text-green-800';
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'PENDING_APPROVAL': return 'bg-blue-100 text-blue-800';
      case 'EXPIRED': return 'bg-red-100 text-red-800';
      case 'TERMINATED': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  const formatRent = (amount: number, frequency: string) => {
    const freq = frequency?.toLowerCase() || 'week';
    return `$${amount?.toLocaleString() || 0}/${freq.replace('ly', '')}`;
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!lease) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <p className="text-gray-600">Lease not found</p>
        <button onClick={() => navigate('/agent/leases')} className="mt-4 text-secondary hover:text-primary">
          Back to Leases
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/agent/leases')} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-gray-900">Lease Details</h1>
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(lease.status)}`}>
              {lease.status}
            </span>
          </div>
          <p className="text-gray-600">{lease.propertyAddress || lease.property?.address}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/agent/leases/${id}/edit`)}
            className="flex items-center gap-2 px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90"
          >
            <Edit className="w-4 h-4" />
            Edit
          </button>
          {lease.status === 'ACTIVE' && (
            <button
              onClick={() => setShowTerminateModal(true)}
              className="flex items-center gap-2 px-4 py-2 border border-orange-300 text-orange-600 rounded-lg hover:bg-orange-50"
            >
              <Clock className="w-4 h-4" />
              Terminate
            </button>
          )}
          <button
            onClick={() => setShowDeleteModal(true)}
            className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>

      {/* Expiry Warning */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-800">{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Expiry Warning */}
      {lease.daysUntilExpiry !== undefined && lease.daysUntilExpiry <= 30 && lease.daysUntilExpiry > 0 && (
        <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-orange-600" />
          <span className="text-orange-800">
            This lease expires in {lease.daysUntilExpiry} days ({formatDate(lease.endDate)})
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Lease Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-gray-900">Lease Information</h2>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Start Date</p>
                <p className="font-medium text-gray-900">{formatDate(lease.startDate)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">End Date</p>
                <p className="font-medium text-gray-900">{lease.endDate ? formatDate(lease.endDate) : 'Open-ended'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Rent Amount</p>
                <p className="font-medium text-gray-900">{formatRent(lease.rentAmount, lease.rentFrequency)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Payment Frequency</p>
                <p className="font-medium text-gray-900">{lease.rentFrequency}</p>
              </div>
              {lease.securityDeposit && (
                <div>
                  <p className="text-sm text-gray-500">Security Deposit</p>
                  <p className="font-medium text-gray-900">${lease.securityDeposit.toLocaleString()}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(lease.status)}`}>
                  {lease.status}
                </span>
              </div>
            </div>
          </div>

          {/* Property Details */}
          {lease.property && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-2 mb-4">
                <Building className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-gray-900">Property</h2>
              </div>
              <div className="space-y-2">
                <p className="font-medium text-gray-900">{lease.property.address}</p>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span>{lease.property.type || 'Property'}</span>
                  {lease.property.bedrooms && <span>{lease.property.bedrooms} beds</span>}
                  {lease.property.bathrooms && <span>{lease.property.bathrooms} baths</span>}
                </div>
                <button
                  onClick={() => navigate(`/agent/properties/${lease.property?.id}`)}
                  className="text-sm text-secondary hover:text-primary"
                >
                  View Property →
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Tenant Info */}
          {lease.tenant && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-2 mb-4">
                <User className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-gray-900">Tenant</h2>
              </div>
              <div className="space-y-3">
                <p className="font-medium text-gray-900">{lease.tenant.name || lease.tenantName || 'Unknown'}</p>
                {(lease.tenant.email || lease.tenantEmail) && (
                  <a href={`mailto:${lease.tenant.email || lease.tenantEmail}`} className="flex items-center gap-2 text-sm text-secondary hover:text-primary">
                    <Mail className="w-4 h-4" /> {lease.tenant.email || lease.tenantEmail}
                  </a>
                )}
                {lease.tenant.phone && (
                  <a href={`tel:${lease.tenant.phone}`} className="flex items-center gap-2 text-sm text-secondary hover:text-primary">
                    <Phone className="w-4 h-4" /> {lease.tenant.phone}
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Owner Info */}
          {lease.ownerName && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-2 mb-4">
                <Building className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-gray-900">Owner</h2>
              </div>
              <p className="font-medium text-gray-900">{lease.ownerName}</p>
            </div>
          )}

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Send Message</h2>
            <div className="space-y-2">
              {lease.tenant && (
                <button
                  onClick={() => navigate(`/messages?type=LEASE&id=${lease.id}&to=tenant`)}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg flex items-center gap-2"
                >
                  <User className="w-4 h-4 text-primary" />
                  Message Tenant
                </button>
              )}
              {lease.ownerName && (
                <button
                  onClick={() => navigate(`/messages?type=LEASE&id=${lease.id}&to=owner`)}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg flex items-center gap-2"
                >
                  <Building className="w-4 h-4 text-primary" />
                  Message Owner
                </button>
              )}
            </div>

            <h2 className="text-lg font-semibold text-gray-900 mb-4 mt-6">Other Actions</h2>
            <div className="space-y-2">
              <button
                onClick={() => navigate(`/agent/inspections/create?propertyId=${lease.propertyId || lease.property?.id}`)}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
              >
                Schedule Inspection
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Delete Lease</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to permanently delete this lease? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Terminate Modal */}
      {showTerminateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-full">
                  <Clock className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Terminate Lease</h3>
              </div>
              <button onClick={() => setShowTerminateModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-gray-600 mb-4">
              This will mark the lease as terminated. The lease record will be preserved for historical purposes.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Reason (optional)</label>
              <textarea
                value={terminateReason}
                onChange={(e) => setTerminateReason(e.target.value)}
                rows={3}
                placeholder="Enter reason for termination..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent resize-none"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowTerminateModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleTerminate}
                disabled={terminating}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
              >
                {terminating ? 'Terminating...' : 'Terminate Lease'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


